import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Eye, Download, CheckCircle, XCircle, AlertTriangle, Search,
  Tag, Calendar, User, Clock, Image, Link, Hash, List, Bold, Italic,
  Quote, ArrowLeft, ArrowRight, Star
} from 'lucide-react';

type Category = 'Entrepreneurship' | 'Marketing' | 'Sales' | 'Business Growth' | 'Technology' | 'Success Stories';

const CATEGORIES: Category[] = ['Entrepreneurship', 'Marketing', 'Sales', 'Business Growth', 'Technology', 'Success Stories'];

const HOOK_OPTIONS = [
  { id: 'question', label: 'Question', desc: 'Start with a provocative question' },
  { id: 'statistic', label: 'Statistic', desc: 'Lead with a surprising data point' },
  { id: 'story', label: 'Story', desc: 'Open with a brief anecdote' },
  { id: 'bold_statement', label: 'Bold Statement', desc: 'Make a strong declarative claim' },
];

type Frontmatter = {
  title: string;
  slug: string;
  date: string;
  author: string;
  category: string;
  tags: string[];
  metaDescription: string;
  featuredImage: string;
  readingTime: string;
};

type ContentSection = {
  id: string;
  type: 'introduction' | 'h2' | 'h3' | 'paragraph' | 'bullet_list' | 'numbered_list' | 'callout' | 'pull_quote' | 'cta';
  content: string;
  items?: string[];
};

type SEOScore = {
  total: number;
  max: number;
  checks: { label: string; passed: boolean; weight: number }[];
};

const initialFrontmatter: Frontmatter = {
  title: '',
  slug: '',
  date: new Date().toISOString().split('T')[0],
  author: '',
  category: '',
  tags: [],
  metaDescription: '',
  featuredImage: '',
  readingTime: '5',
};

const initialSections: ContentSection[] = [
  { id: 'intro', type: 'introduction', content: '' },
  { id: 's1', type: 'h2', content: '' },
  { id: 's1p1', type: 'paragraph', content: '' },
  { id: 's1p2', type: 'paragraph', content: '' },
  { id: 's2', type: 'h2', content: '' },
  { id: 's2p1', type: 'paragraph', content: '' },
  { id: 's2b1', type: 'bullet_list', content: '', items: ['', '', ''] },
  { id: 's3', type: 'h2', content: '' },
  { id: 's3p1', type: 'paragraph', content: '' },
  { id: 'conclusion', type: 'paragraph', content: '' },
  { id: 'cta', type: 'cta', content: '' },
];

const SECTION_LABELS: Record<string, string> = {
  introduction: 'Introduction',
  h2: 'H2 Subheading',
  h3: 'H3 Subheading',
  paragraph: 'Body Text',
  bullet_list: 'Bullet List',
  numbered_list: 'Numbered List',
  callout: 'Callout Box',
  pull_quote: 'Pull Quote',
  cta: 'CTA Section',
};

function calculateSEOScore(frontmatter: Frontmatter, sections: ContentSection[]): SEOScore {
  const textContent = sections.map((s) => s.content).join(' ');
  const allText = frontmatter.title + ' ' + frontmatter.metaDescription + ' ' + textContent;
  const primaryKeyword = frontmatter.title.split(' ').slice(0, 3).join(' ');

  const checks = [
    { label: 'Title (H1) contains primary keyword', passed: frontmatter.title.toLowerCase().includes(primaryKeyword.toLowerCase()), weight: 15 },
    { label: 'Meta description filled', passed: frontmatter.metaDescription.length > 0, weight: 10 },
    { label: 'Meta description \u2264160 chars', passed: frontmatter.metaDescription.length <= 160 && frontmatter.metaDescription.length > 0, weight: 10 },
    { label: 'Meta description contains keyword', passed: frontmatter.metaDescription.toLowerCase().includes(primaryKeyword.toLowerCase()), weight: 10 },
    { label: 'Keyword in first 100 words', passed: allText.split(' ').slice(0, 100).join(' ').toLowerCase().includes(primaryKeyword.toLowerCase()), weight: 10 },
    { label: 'URL slug filled', passed: frontmatter.slug.length > 0, weight: 5 },
    { label: 'Categories selected', passed: frontmatter.category.length > 0, weight: 5 },
    { label: 'Tags added (2+)', passed: frontmatter.tags.length >= 2, weight: 5 },
    { label: 'Featured image set', passed: frontmatter.featuredImage.length > 0, weight: 5 },
    { label: 'Author filled', passed: frontmatter.author.length > 0, weight: 5 },
    { label: 'Reading time set', passed: frontmatter.readingTime.length > 0, weight: 5 },
    { label: 'H2 subheadings present', passed: sections.some((s) => s.type === 'h2' && s.content.trim()), weight: 5 },
    { label: 'Bullet/numbered lists used', passed: sections.some((s) => (s.type === 'bullet_list' || s.type === 'numbered_list')), weight: 5 },
    { label: 'Content body written', passed: sections.some((s) => s.type === 'paragraph' && s.content.trim().length > 50), weight: 5 },
  ];

  const total = checks.reduce((sum, c) => sum + (c.passed ? c.weight : 0), 0);
  const max = checks.reduce((sum, c) => sum + c.weight, 0);

  return { total, max, checks };
}

function renderSectionToMarkdown(section: ContentSection): string {
  switch (section.type) {
    case 'introduction':
      return `## Introduction\n\n${section.content}\n`;
    case 'h2':
      return `\n## ${section.content}\n`;
    case 'h3':
      return `\n### ${section.content}\n`;
    case 'paragraph':
      return `${section.content}\n`;
    case 'bullet_list':
      return (section.items || []).filter(Boolean).map((item) => `- ${item}`).join('\n') + '\n';
    case 'numbered_list':
      return (section.items || []).filter(Boolean).map((item, i) => `${i + 1}. ${item}`).join('\n') + '\n';
    case 'callout':
      return `> **Key Point**: ${section.content}\n`;
    case 'pull_quote':
      return `> *"${section.content}"*\n`;
    case 'cta':
      return `\n**${section.content}**\n\n[BizBuilder CTA - link to relevant tier/feature]\n`;
    default:
      return '';
  }
}

function sectionsToMarkdown(frontmatter: Frontmatter, sections: ContentSection[]): string {
  const fm = `---
title: "${frontmatter.title}"
slug: "/${frontmatter.slug}"
date: "${frontmatter.date}"
author: "${frontmatter.author}"
category: "${frontmatter.category}"
tags: [${frontmatter.tags.map((t) => `"${t}"`).join(', ')}]
meta_description: "${frontmatter.metaDescription}"
featured_image: "${frontmatter.featuredImage}"
reading_time: "${frontmatter.readingTime} minutes"
---

# ${frontmatter.title}

**Reading time**: ${frontmatter.readingTime} minutes
**Category**: ${frontmatter.category || 'Uncategorized'}

`;

  const body = sections.map(renderSectionToMarkdown).join('\n');

  const authorBio = `

---

**About the Author**

${frontmatter.author} is a business coach at Chrysalinks Group, helping lifestyle entrepreneurs
10x their revenues through the BizBuilder Platform.
`;

  return fm + body + authorBio;
}

export const BlogPostTemplate: React.FC = () => {
  const [frontmatter, setFrontmatter] = useState<Frontmatter>(initialFrontmatter);
  const [sections, setSections] = useState<ContentSection[]>(initialSections);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [showPublishingChecklist, setShowPublishingChecklist] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [copied, setCopied] = useState(false);

  const updateFM = <K extends keyof Frontmatter>(key: K, value: Frontmatter[K]) => {
    setFrontmatter((prev) => ({ ...prev, [key]: value }));
  };

  const updateSection = (id: string, updates: Partial<ContentSection>) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (tag && !frontmatter.tags.includes(tag)) {
      setFrontmatter((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFrontmatter((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const addSection = (type: ContentSection['type']) => {
    const newId = `s${Date.now()}`;
    const newSection: ContentSection = { id: newId, type, content: '', items: ['', '', ''] };
    setSections((prev) => [...prev.slice(0, -2), newSection, ...prev.slice(-2)]);
  };

  const seoScore = useMemo(() => calculateSEOScore(frontmatter, sections), [frontmatter, sections]);
  const seoPercent = Math.round((seoScore.total / seoScore.max) * 100);

  const publishingReady = useMemo(() => {
    return (
      seoPercent >= 80 &&
      frontmatter.metaDescription.length > 0 &&
      frontmatter.featuredImage.length > 0 &&
      frontmatter.category.length > 0 &&
      frontmatter.tags.length >= 2
    );
  }, [seoPercent, frontmatter]);

  const markdown = useMemo(() => sectionsToMarkdown(frontmatter, sections), [frontmatter, sections]);

  const handleCopyMarkdown = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = markdown;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [markdown]);

  const handleExportMarkdown = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${frontmatter.slug || 'blog-post'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#6B5E2B] flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#2C3E50]" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
                Blog Post Template
              </h1>
              <p className="text-xs text-slate-500">SEO-optimized content framework</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button onClick={() => setActiveTab('edit')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'edit' ? 'bg-white shadow-sm text-[#2C3E50]' : 'text-slate-500 hover:text-slate-700'}`}>
                Edit
              </button>
              <button onClick={() => setActiveTab('preview')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'preview' ? 'bg-white shadow-sm text-[#2C3E50]' : 'text-slate-500 hover:text-slate-700'}`}>
                <Eye size={14} className="inline mr-1" /> Preview
              </button>
            </div>
            <button onClick={handleCopyMarkdown}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
              {copied ? <CheckCircle size={16} className="text-green-500" /> : <FileText size={16} />}
              {copied ? 'Copied!' : 'Copy MD'}
            </button>
            <button onClick={handleExportMarkdown}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: '#6B5E2B' }}>
              <Download size={16} /> Export .md
            </button>
          </div>
        </div>
      </div>

      {/* SEO Score Bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${
              seoPercent >= 80 ? 'bg-green-100 text-green-700' :
              seoPercent >= 50 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'}`}>
              {seoPercent}%
            </div>
            <div>
              <p className="text-sm font-medium text-[#2C3E50]">SEO Score</p>
              <p className="text-xs text-slate-400">{seoScore.total}/{seoScore.max} checks passed</p>
            </div>
          </div>
          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${
              seoPercent >= 80 ? 'bg-green-500' : seoPercent >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${seoPercent}%` }} />
          </div>
          <button onClick={() => setShowPublishingChecklist(!showPublishingChecklist)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
            <List size={16} /> Checklist
          </button>
        </div>
      </div>

      {/* Publishing Checklist */}
      <AnimatePresence>
        {showPublishingChecklist && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="bg-white border-b border-slate-200 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <h3 className="text-sm font-bold text-[#2C3E50] mb-3">Publishing Checklist</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { label: 'SEO score \u2265 80', passed: seoPercent >= 80 },
                  { label: 'Meta description filled', passed: frontmatter.metaDescription.length > 0 },
                  { label: 'Meta description \u2264 160 chars', passed: frontmatter.metaDescription.length <= 160 },
                  { label: 'Featured image set', passed: frontmatter.featuredImage.length > 0 },
                  { label: 'Categories selected', passed: frontmatter.category.length > 0 },
                  { label: 'Tags added (2+)', passed: frontmatter.tags.length >= 2 },
                  { label: 'Internal links included', passed: sections.some((s) => s.content.includes('BizBuilder') || s.content.includes('chrysalinks')) },
                  { label: 'Author filled', passed: frontmatter.author.length > 0 },
                  { label: 'URL slug filled', passed: frontmatter.slug.length > 0 },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {item.passed ? (
                      <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle size={14} className="text-slate-300 flex-shrink-0" />
                    )}
                    <span className={item.passed ? 'text-slate-700' : 'text-slate-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
              <div className={`mt-3 p-3 rounded-lg text-sm ${publishingReady ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                {publishingReady
                  ? '\u2705 All checks passed! Ready to publish.'
                  : '\u26a0\ufe0f Complete all checklist items before publishing.'}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'edit' ? (
          <div className="grid grid-cols-3 gap-6">
            {/* Frontmatter column */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-[#2C3E50] mb-4 flex items-center gap-2">
                  <Hash size={14} /> Frontmatter
                </h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Title</label>
                    <input value={frontmatter.title} onChange={(e) => updateFM('title', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" placeholder="Primary Keyword — Benefit" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Slug</label>
                      <input value={frontmatter.slug} onChange={(e) => updateFM('slug', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" placeholder="keyword-slug" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
                      <input type="date" value={frontmatter.date} onChange={(e) => updateFM('date', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Author</label>
                      <input value={frontmatter.author} onChange={(e) => updateFM('author', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" placeholder="Author name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Reading Time (min)</label>
                      <input value={frontmatter.readingTime} onChange={(e) => updateFM('readingTime', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
                    <select value={frontmatter.category} onChange={(e) => updateFM('category', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]">
                      <option value="">Select category</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Tags</label>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {frontmatter.tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                          {tag}
                          <button onClick={() => removeTag(tag)} className="hover:text-red-500">&times;</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <input value={newTag} onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addTag()}
                        className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" placeholder="Add tag..." />
                      <button onClick={addTag} className="px-3 py-2 bg-slate-100 rounded-lg text-sm hover:bg-slate-200">+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Meta Description <span className="text-slate-400">({frontmatter.metaDescription.length}/160)</span>
                    </label>
                    <textarea value={frontmatter.metaDescription} onChange={(e) => updateFM('metaDescription', e.target.value)}
                      rows={3} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B] resize-none"
                      placeholder="150-160 characters with primary keyword..." />
                    <div className={`h-1 mt-1 rounded-full ${frontmatter.metaDescription.length <= 160 ? 'bg-green-200' : 'bg-red-200'}`}>
                      <div className={`h-full rounded-full ${frontmatter.metaDescription.length <= 160 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(100, (frontmatter.metaDescription.length / 160) * 100)}%` }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Featured Image URL</label>
                    <input value={frontmatter.featuredImage} onChange={(e) => updateFM('featuredImage', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B]" placeholder="https://..." />
                  </div>
                </div>
              </div>

              {/* SEO Check Details */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-[#2C3E50] mb-4 flex items-center gap-2">
                  <Search size={14} /> SEO Checks
                </h2>
                <div className="space-y-2">
                  {seoScore.checks.map((check, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      {check.passed ? (
                        <CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle size={12} className="text-slate-300 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <span className={check.passed ? 'text-slate-600' : 'text-slate-400'}>{check.label}</span>
                        <span className="text-slate-300 ml-1">({check.weight}pts)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content editing column */}
            <div className="col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-[#2C3E50] flex items-center gap-2"><Bold size={14} /> Content</h2>
                  <div className="flex gap-1">
                    {['h2', 'h3', 'bullet_list', 'numbered_list', 'callout', 'pull_quote'].map((type) => (
                      <button key={type} onClick={() => addSection(type as ContentSection['type'])}
                        className="px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50 text-slate-500">
                        +{SECTION_LABELS[type] || type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {sections.map((section) => (
                    <div key={section.id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-[#6B5E2B] uppercase tracking-wider">
                          {SECTION_LABELS[section.type]}
                        </span>
                        <button onClick={() => setSections((prev) => prev.filter((s) => s.id !== section.id))}
                          className="text-xs text-slate-400 hover:text-red-500">&times; Remove</button>
                      </div>
                      {section.type === 'bullet_list' || section.type === 'numbered_list' ? (
                        <div className="space-y-1">
                          {(section.items || ['', '', '']).map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-slate-400 text-xs">{section.type === 'bullet_list' ? '\u2022' : `${i + 1}.`}</span>
                              <input value={item} onChange={(e) => {
                                const items = [...(section.items || ['', '', ''])];
                                items[i] = e.target.value;
                                updateSection(section.id, { items });
                              }} className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-[#6B5E2B]" />
                              <button onClick={() => {
                                const items = [...(section.items || [])];
                                items.splice(i, 1);
                                updateSection(section.id, { items });
                              }} className="text-slate-300 hover:text-red-500 text-xs">&times;</button>
                            </div>
                          ))}
                          <button onClick={() => {
                            const items = [...(section.items || []), ''];
                            updateSection(section.id, { items });
                          }} className="text-xs text-[#6B5E2B] hover:underline mt-1">+ Add item</button>
                        </div>
                      ) : (
                        <textarea value={section.content} onChange={(e) => updateSection(section.id, { content: e.target.value })}
                          rows={section.type === 'introduction' || section.type === 'paragraph' || section.type === 'cta' ? 4 : 2}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#6B5E2B] resize-none"
                          placeholder={`Enter ${SECTION_LABELS[section.type].toLowerCase()} content...`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Preview mode */
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {frontmatter.featuredImage && (
                <div className="h-48 bg-slate-100 flex items-center justify-center overflow-hidden">
                  <img src={frontmatter.featuredImage} alt={frontmatter.title} className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              )}
              <div className="p-8">
                {frontmatter.category && (
                  <span className="inline-block px-3 py-1 bg-[#6B5E2B]/10 text-[#6B5E2B] text-xs font-semibold rounded-full mb-4">
                    {frontmatter.category}
                  </span>
                )}
                <h1 className="text-3xl font-bold text-[#2C3E50] mb-4" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
                  {frontmatter.title || 'Untitled Post'}
                </h1>
                <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
                  {frontmatter.author && <span className="flex items-center gap-1"><User size={14} /> {frontmatter.author}</span>}
                  {frontmatter.date && <span className="flex items-center gap-1"><Calendar size={14} /> {frontmatter.date}</span>}
                  {frontmatter.readingTime && <span className="flex items-center gap-1"><Clock size={14} /> {frontmatter.readingTime} min read</span>}
                </div>

                {frontmatter.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {frontmatter.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-500">#{tag}</span>
                    ))}
                  </div>
                )}

                <hr className="border-slate-200 mb-6" />

                <div className="prose prose-slate max-w-none prose-headings:text-[#2C3E50] prose-headings:font-bold prose-a:text-[#6B5E2B]">
                  {sections.map((section) => {
                    const content = section.content;
                    if (!content && (!section.items || section.items.every((i) => !i))) return null;
                    switch (section.type) {
                      case 'introduction':
                        return <p key={section.id} className="text-lg leading-relaxed text-slate-700 mb-6">{content}</p>;
                      case 'h2':
                        return <h2 key={section.id} className="text-xl font-bold mt-8 mb-4" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>{content}</h2>;
                      case 'h3':
                        return <h3 key={section.id} className="text-lg font-bold mt-6 mb-3">{content}</h3>;
                      case 'paragraph':
                        return <p key={section.id} className="text-slate-700 leading-relaxed mb-4">{content}</p>;
                      case 'bullet_list':
                        return (
                          <ul key={section.id} className="list-disc pl-6 mb-4 space-y-1 text-slate-700">
                            {section.items?.filter(Boolean).map((item, i) => <li key={i}>{item}</li>)}
                          </ul>
                        );
                      case 'numbered_list':
                        return (
                          <ol key={section.id} className="list-decimal pl-6 mb-4 space-y-1 text-slate-700">
                            {section.items?.filter(Boolean).map((item, i) => <li key={i}>{item}</li>)}
                          </ol>
                        );
                      case 'callout':
                        return (
                          <div key={section.id} className="p-4 bg-[#6B5E2B]/5 border-l-4 border-[#6B5E2B] rounded-r-lg mb-4">
                            <p className="text-sm font-bold text-[#2C3E50]">Key Point:</p>
                            <p className="text-sm text-slate-600">{content}</p>
                          </div>
                        );
                      case 'pull_quote':
                        return (
                          <blockquote key={section.id} className="border-l-4 border-[#6B5E2B] pl-6 my-6 italic text-lg text-slate-600" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
                            &ldquo;{content}&rdquo;
                          </blockquote>
                        );
                      case 'cta':
                        return (
                          <div key={section.id} className="mt-8 p-6 rounded-lg text-center" style={{ background: 'linear-gradient(135deg, #6B5E2B 0%, #8B7E3B 100%)' }}>
                            <p className="text-white font-bold text-lg mb-3">{content}</p>
                            <span className="inline-block px-6 py-3 bg-white text-[#6B5E2B] rounded-lg font-bold text-sm">
                              Get Started with BizBuilder
                            </span>
                          </div>
                        );
                      default:
                        return null;
                    }
                  })}
                </div>

                <hr className="border-slate-200 my-8" />

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-bold text-[#2C3E50] mb-1">About the Author</p>
                  <p className="text-sm text-slate-600">
                    {frontmatter.author || '[Author Name]'} is a business coach at Chrysalinks Group, helping lifestyle entrepreneurs
                    10x their revenues through the BizBuilder Platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
